import React, { useEffect, useState } from 'react';

import { MultiSelect, MultiSelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteEventType from 'hooks/eventTypes/useAutoCompleteEventType';
import { EventType } from 'types';

export interface IEventTypeMultiSelectAutoComplete extends EventType {}

interface EventTypeMultiSelectAutoCompleteProps extends Omit<MultiSelectProps, 'data'> {
  values: IEventTypeMultiSelectAutoComplete[];
  handleChange: (values: IEventTypeMultiSelectAutoComplete[]) => void;
}

const EventTypeMultiSelectAutoComplete = (props: EventTypeMultiSelectAutoCompleteProps): JSX.Element => {
  const { values, handleChange, ...msProps } = props;
  const [autocompleteData, setAutocompleteData] = useState<IEventTypeMultiSelectAutoComplete[]>([]);
  const [search, setSearch] = useState<string>('');
  const [debouncedEventSearchTerm] = useDebouncedValue<string>(search, 250);
  const { data, isSuccess } = useAutoCompleteEventType({ search: debouncedEventSearchTerm });

  const onChange = (selectedItems: string[]): void => {
    const selectedEventTypes = autocompleteData.filter(eventType =>
      selectedItems.includes(eventType.eventTypeId),
    );
    handleChange(selectedEventTypes);
  };

  useEffect(() => {
    if (isSuccess && data) {
      const updatedData = data.data.results.concat(
        values.filter(v => !data.data.results.some(eventType => eventType.eventTypeId === v.eventTypeId)),
      );
      setAutocompleteData(updatedData);
    }
  }, [isSuccess, data, values]);

  return (
    <MultiSelect
      placeholder='Select event types'
      {...msProps}
      value={values.map(v => v.eventTypeId)}
      onChange={onChange}
      data={autocompleteData.map(eventType => ({
        value: eventType.eventTypeId,
        label: eventType.eventTypeName,
        'data-cy': `${eventType.eventTypeName}`,
      }))}
      clearable
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      nothingFound='No event types found'
      data-cy='selectEventTypesMultiSelect'
    />
  );
};

export default EventTypeMultiSelectAutoComplete;
